/**
 * DEFUSE S10 — the per-kind block-child transform switch
 * (`transformBlockChild`), the typed twin of the legacy bag amplifier. Split
 * out of `./blockTransform.ts` (the barrel: public surface + the family's
 * module documentation, including the per-kind conventions, the
 * mirrored-sweep / elliptic-promotion rows and the bulge and spline notes this
 * arm implements) purely for file size; every arm is unchanged.
 */

import { isCustomXAxisDirection } from "@dxf-ai-editor/command-engine";
import {
  dxfVertexSubtype,
  fitPointsToControlPoints,
  ocsPlanarTransform,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";
import type {
  BlockChildEntity,
  OcsPlanarTransform,
  PolylineVertexRecord,
} from "@dxf-ai-editor/drawing-model";

import type { Vec3 } from "../../types";

import {
  DEG_TO_RAD,
  blockScalarRotationDeg,
  isMirroredTransform,
  normalizeDeg,
  transformBlockChildPoint,
  transformBlockDirection,
  type BlockInsertTransform,
} from "./blockInsertTransform";
import {
  MIN_PROMOTABLE_SWEEP_RAD,
  arcCcwSweepDeg,
  arcChildAsLocalEllipse,
  mapDirectionAngleDeg,
  mapDirectionRad,
  mapUniformArcAngleDeg,
  transformEllipseGeometry,
} from "./blockArcEllipseMapping";
import { blockChildBandFrame } from "./blockChildBandFrame";
import { blockChildThicknessSweep } from "./blockChildThicknessSweep";
import {
  annotativeContextsOverride,
  resolveBlockChildLayer,
  type BlockChildIdentity,
} from "./blockChildIdentity";
import { transformHatchEdge } from "./blockHatchEdge";

/**
 * Transform one typed leaf block-child into world space by cloning it with
 * transformed geometry fields — the typed twin of the legacy bag amplifier
 * `blockChildToEntityPreview` (`blockInsert.ts:342-533`), extended to the
 * kinds the amplifier dropped (Ellipse / Spline / Hatch / Revision Cloud /
 * Point). The clone is then fed to `entityToRenderableTyped`, so the
 * per-kind typed adapters stay the single leaf-conversion truth.
 *
 * Returns `null` for kinds without a typed leaf adapter:
 *   - `Block`  — the caller recurses BEFORE dispatch (plan §4.6 Phase-B
 *     carve-out 2; the dispatcher would silently null it);
 *   - `Attdef` — Phase-B carve-out 1 (non-constant ATTDEF is never rendered
 *     standalone; the value/constant render is the S10c slice);
 *   - `Multileader` — the caller intercepts it BEFORE this dispatch (Codex PR
 *     #784 round 4 F1): a MULTILEADER's geometry is DERIVED from scalar style
 *     fields and, for `contentType === 1`, re-enters the block flatten, so it
 *     is cloned into world space as an ENTITY and dispatched to the real typed
 *     adapter by `./blockChildMultileader.ts` (which `flattenTypedInsert`
 *     calls, exactly like the `Dimension` arm below). `null` here guards a
 *     stray direct caller from emitting a half-transformed callout.
 *
 *   - `Table`  — intercepted by `flattenTypedInsert` BEFORE this dispatch and
 *     rendered by `./blockChildTable.ts` (@codex PR #785 round 1, F1): an
 *     `ACAD_TABLE` IS a block reference to its anonymous `*T<N>` graphics
 *     block, so it is placed by COMPOSING the enclosing INSERT affine onto that
 *     reference (`AcDbTable` carries no scale factors of its own, so a clone
 *     could not carry the enclosing scale at all) and flattening once. `null`
 *     here guards a stray direct caller from emitting a table whose content
 *     never rode the insert transform.
 *
 *   - `OLE Frame` — intercepted by `flattenTypedInsert` BEFORE this dispatch
 *     and rendered by `./blockChildOleFrame.ts` (@codex PR #785 round 2, F1):
 *     the entity stores TWO opposite corners and no rotation, so a clone can
 *     only describe an AXIS-ALIGNED box, while the image of the rectangle
 *     under an oblique INSERT rotation is a ROTATED one. The four corners are
 *     mapped and drawn as that quad instead (see the arm below for the full
 *     rationale, including why the BOUNDS side keeps its AABB).
 *
 * `Dimension` returns `null` HERE — it is rendered by
 * `renderBlockChildDimension` (`blockFromEntity.ts`), which generates the
 * dimension in LOCAL space then rigidly transforms each emitted primitive by
 * the block affine (the AutoCAD "*D anonymous-block bake, then transform the
 * INSERT" model). Point-transforming the definition points + re-deriving in
 * world (the prior approach) could NOT carry the block SCALE into the
 * DIMSTYLE-derived arrow/extension/gap/text scalars (DIMASZ / DIMEXO / DIMEXE /
 * DIMGAP / DIMTXT) nor the block ROTATION into the horizontal-only linear
 * emitter, and destroyed the local `0`-sentinel defPoint / absent-extension
 * fallbacks (@codex #510 ×4) — see {@link transformDimensionPrimitive}.
 */
export function transformBlockChild(
  child: BlockChildEntity,
  basePoint: Vec3,
  t: BlockInsertTransform,
  identity: BlockChildIdentity,
): BlockChildEntity | null {
  // Identity + AutoCAD layer-0 inheritance, spread over every clone: the
  // rewritten `layer` makes ByLayer colour/linetype resolve against the
  // inherited layer automatically in `buildRenderableShellTyped` (and puts
  // the renderable in the right visibility/freeze bucket after S11).
  const resolved = {
    id: identity.id,
    spaceId: identity.spaceId,
    layer: resolveBlockChildLayer(child.layer, identity.parentLayer),
  };
  const pt = (p: { x: number; y: number; z?: number }): Vec3 =>
    transformBlockChildPoint(p, basePoint, t);
  const pt2 = (p: { x: number; y: number }): { x: number; y: number } => {
    const v = pt(p);
    return { x: v.x, y: v.y };
  };
  /** Legacy scalar conventions (see module header). */
  const radiusScale = Math.abs(t.scale.x);
  const heightScale = Math.abs(t.scale.y);
  const widthScale = Math.abs(t.scale.x);
  /**
   * The width factor for the fields that become a drawn BAND — the LWPOLYLINE
   * / heavy POLYLINE groups 40 / 41 / 43 and the entity-level defaults
   * (@codex PR #785 round 6, P2).
   *
   * `|scale.x|` is the EXACT image of a width under a SIMILARITY (`|sx| ===
   * |sy|`, mirror included) and stays byte-identical there — the overwhelming
   * common case. Under anything else a width is not a scalar at all: the image
   * of a constant-width band under `S(2, 3)` reaches 3 half-widths in world Y
   * where this factor claims 2, so the authored width is carried through
   * UNSCALED and `./blockChildBandFrame.ts` hands the render path the affine to
   * build the band with — the same thing the vector plot has always done
   * (`print/pdf/converters/insertConverter.ts`: offset in the block's local
   * frame, then map the finished loops). That module owns the decision for
   * BOTH sides, so the width can never be scaled AND the band mapped.
   *
   * The frame is `null` for every kind that carries no band, so Revision
   * Cloud's `constantWidth` (procedural scallops — its geometry has no `widths`
   * field at all) and the MTEXT `referenceWidth` wrap column below keep the
   * plain `widthScale`, unchanged.
   */
  const bandWidthScale = blockChildBandFrame(child, t) ? 1 : widthScale;
  /** TEXT glyph aspect (@codex #431 P2): height carries |scaleY| (legacy
   *  convention), so the width factor must carry the REMAINING |sx|/|sy|
   *  ratio for the horizontal metric to equal height·widthFactor·|scaleX|.
   *  1 under uniform scale (incl. mirror) → golden-parity untouched. */
  const widthFactorScale = Math.abs(t.scale.x) / Math.abs(t.scale.y);
  const mirrored = isMirroredTransform(t);
  /** Effective rotation for the SCALAR angle-sum convention (@codex #431
   *  round-6): `S(−s,−s) ≡ R(180°)·S(s,s)` — a DOUBLE-negative uniform scale
   *  is a point reflection (det > 0, NOT a mirror), so every scalar angle
   *  sum (Arc sweep, TEXT/MText rotation) folds an extra 180°; positions
   *  already point-reflect through the negative components. The vector-based
   *  paths (ellipse conjugate axes, MTEXT custom direction, the
   *  `mapUniformArcAngleDeg`/`mapDirection*` tables) handle the case
   *  natively and do NOT use this. Single-negative (true mirror, det < 0)
   *  deliberately stays the raw sum — the legacy-pinned convention
   *  (S10-XFORM owns mirror reflection, module header). ONE truth —
   *  {@link blockScalarRotationDeg}, which also folds the optional inner
   *  rotation (`preRotationDeg`) with the reflection sign rule. */
  const scalarRotationDeg = blockScalarRotationDeg(t);
  /** Common z for planar entities that store it as `elevation` (LWPOLYLINE /
   *  Hatch / Revision Cloud) — the legacy z-collapse applied to the plane. */
  const elevationOf = (elevation: number | undefined): number =>
    ((elevation ?? 0) - basePoint.z) * t.scale.z + t.insertion.z;
  /** Bulge is rotation/uniform-scale invariant and flips sign under mirror.
   *  A zero bulge stays +0 (no `-0` — the golden harness compares exact). */
  const mapBulge = (bulge: number | undefined): number => {
    const b = bulge ?? 0;
    return mirrored && b !== 0 ? -b : b;
  };
  /**
   * A group-50 curve-fit tangent through the OCS PLANE map — the OCS twin of
   * {@link mapDirectionAngleDeg}, reading the SAME in-plane basis pair the
   * vertices are re-expressed through, so a tangent can never end up describing
   * a plane the vertices no longer live in. (No exact-table fast path: the
   * golden-parity harness only pins the DEFAULT-extrusion flatten, which never
   * reaches here.)
   */
  const ocsTangentDeg = (deg: number, ocs: OcsPlanarTransform): number => {
    const rad = deg * DEG_TO_RAD;
    const d = ocs.direction(Math.cos(rad), Math.sin(rad));
    return normalizeDeg((Math.atan2(d.y, d.x) * 180) / Math.PI);
  };

  /** One truth for `polylineVertexSchema` lists (LWPOLYLINE + Revision
   *  Cloud share the on-disk vertex shape): position through the affine,
   *  bulge sign per mirror, widths per the |scaleX| magnitude convention. */
  const mapPolylineVertices = <
    V extends {
      x: number;
      y: number;
      bulge?: number;
      startWidth?: number;
      endWidth?: number;
    },
  >(
    vertices: V[],
  ): V[] =>
    vertices.map((v) => ({
      ...v,
      ...pt2(v),
      bulge: mapBulge(v.bulge),
      startWidth: (v.startWidth ?? 0) * bandWidthScale,
      endWidth: (v.endWidth ?? 0) * bandWidthScale,
    }));

  /**
   * One heavy-`POLYLINE` `VERTEX` record through the insert transform
   * (@codex PR #785 round 1, F1). Deliberately NOT `mapPolylineVertices`: a
   * heavy polyline's 40 / 41 / 42 are genuinely OPTIONAL, and DXF distinguishes
   * "group absent ⇒ inherit the POLYLINE's own default" from "written as 0" —
   * materialising `0` here (which the LWPOLYLINE mapper does, because its
   * schema defaults those fields) would silently hairline a wide polyline and
   * bake a straight segment into every INSERT of the block.
   *
   *   - a 2D polyline is PLANAR at the entity elevation, so only X/Y ride the
   *     affine and the vertex's own Z stays as authored (the plane travels on
   *     `elevation` below — the same split `wcsBoundsLeaf.ts::heavyPolylineLocal`
   *     and `polylineFromEntity.ts` make);
   *   - a 3D polyline / mesh vertex carries real WCS Z, so it rides the FULL
   *     affine;
   *   - a poly-face FACE record carries 71..74 vertex INDICES and a FILLER
   *     10/20/30, so it is passed through untouched — transforming filler would
   *     invent a point, and its winding must not change (a negative index marks
   *     the edge STARTING at that vertex invisible).
   */
  const mapHeavyPolylineVertex = (
    v: PolylineVertexRecord,
    planar: boolean,
    ocs: OcsPlanarTransform | null,
  ): PolylineVertexRecord => {
    if (dxfVertexSubtype(v.flags) === "face-record") return v;
    // On the OCS path the record is LIFTED to WCS, carried through the INSERT
    // and re-expressed in the transformed plane's own OCS, so it lands where
    // `wcsBoundsBlock` puts it (see the `case "Polyline"` arm). The bulge is
    // NOT flipped there: `ocs.linear` always has `det > 0` because the mirror
    // has already been absorbed into the new group-210 normal, so `mapBulge`
    // would apply the reflection a second time.
    const moved = ocs ? ocs.vertex(v.location.x, v.location.y) : null;
    return {
      ...v,
      location: moved
        ? { ...v.location, x: moved.x, y: moved.y, z: moved.z }
        : planar
          ? { ...v.location, ...pt2(v.location) }
          : pt(v.location),
      ...(v.bulge !== undefined
        ? { bulge: ocs ? v.bulge : mapBulge(v.bulge) }
        : {}),
      ...(v.startWidth !== undefined
        ? { startWidth: v.startWidth * bandWidthScale }
        : {}),
      ...(v.endWidth !== undefined
        ? { endWidth: v.endWidth * bandWidthScale }
        : {}),
      // Group 50 is a curve-fit tangent DIRECTION, so it rides the linear part
      // exactly like the hatch pattern angle (mirror reflects, non-uniform
      // scale shears) rather than the scalar angle-sum convention — through the
      // OCS PLANE map when there is one, so it and the vertices speak about the
      // same plane.
      ...(v.curveFitTangent !== undefined
        ? {
            curveFitTangent: ocs
              ? ocsTangentDeg(v.curveFitTangent, ocs)
              : mapDirectionAngleDeg(v.curveFitTangent, t),
          }
        : {}),
    };
  };

  switch (child.type) {
    case "Line":
      return {
        ...child,
        ...resolved,
        start: pt(child.start),
        end: pt(child.end),
      };
    case "Point":
      // Legacy renders Point children at (0,0,0) — the amplifier emits
      // `Location X/Y/Z` keys `pointToRenderable` never reads (§3.4#5). The
      // typed path reads the model directly, so the true transformed
      // location surfaces here (mode:"reference", plan §4.6 r11 Point row).
      return { ...child, ...resolved, location: pt(child.location) };
    case "Circle":
      // Uniform-scale convention `radius · |scaleX|` (legacy `:400`).
      // Non-uniform Circle → ellipse promotion is the S10-XFORM delta.
      return {
        ...child,
        ...resolved,
        center: pt(child.center),
        radius: child.radius * radiusScale,
      };
    case "Arc": {
      // ── NON-UNIFORM enclosing map ⇒ the image is an ELLIPTIC arc ──
      // `radius · |scaleX|` would draw the X-axis radius all the way round (a
      // local r=1 arc under `S(2, 3)` puts its 90° apex at 3, not 2 — while
      // every sibling LINE/LWPOLYLINE vertex of the SAME block rides the full
      // 2×2), so PROMOTE instead of reducing the map to one scalar. The
      // predicate is the SAME `|scaleX| === |scaleY|` similarity test
      // `mapDirectionRad` branches on, so the arms never disagree about which
      // case they are in.
      if (
        Math.abs(t.scale.x) !== Math.abs(t.scale.y) &&
        // The ONE case the ARC and ELLIPSE forms read the same numbers
        // differently: an arc whose CCW sweep is (effectively) ZERO draws a run
        // of COINCIDENT points — nothing — through `sampleArc`, while the DXF
        // ELLIPSE rule reads a degenerate span as a FULL revolution. Every
        // radius draws the same nothing for such an arc, so the scalar
        // reduction is EXACT for it and keeping it circular preserves the
        // pre-existing (invisible) output. A `start → start + 360` FULL circle
        // is NOT this case and promotes like any other arc.
        arcCcwSweepDeg(child.startAngle, child.endAngle) * DEG_TO_RAD >
          MIN_PROMOTABLE_SWEEP_RAD
      ) {
        const local = arcChildAsLocalEllipse(child, basePoint, t.preRotationDeg);
        // φ now lives in `local`'s own frame, so the transform handed on must
        // NOT apply it a second time.
        const outer = t.preRotationDeg ? { ...t, preRotationDeg: 0 } : t;
        return {
          ...local,
          ...resolved,
          ...transformEllipseGeometry(local, basePoint, outer),
        };
      }
      // ── SIMILARITY (incl. mirror) ⇒ the image is still a CIRCLE ──
      // Legacy convention (`:419-422`): radius · |scaleX|, angles shifted by
      // the INSERT rotation — with the round-6 double-negative fold
      // (`scalarRotationDeg`: S(−s,−s) = R(180°), so the sweep shifts 180°;
      // centres/radii are unaffected).
      //
      // MIRROR (det < 0) rides the exact reflection table
      // {@link mapUniformArcAngleDeg} — the SAME one-truth the hatch arc-edge
      // and dimension arc arms use. Its two reflection rows
      // (`sx<0, sy>0: θ′ = rot + 180 − θ`; `sx>0, sy<0: θ′ = rot − θ`) are what
      // the raw sum never had: a reflected arc's endpoint angles are NEGATED,
      // not shifted, so the pre-fix clone landed in the wrong quadrant
      // (`abi1-test.dxf` `*U12` handles 179B / 17BF — the "S-squiggle"). Its
      // NON-mirrored rows are `θ + rot` / `θ + rot + 180`, i.e. EXACTLY
      // `θ + scalarRotationDeg` modulo the table's `normalizeDeg` — and the RAW
      // sum is kept verbatim there because it is the byte-exact
      // golden-parity-vs-legacy baseline the S11 cutover rests on (plan §4.3);
      // normalising would move a 90°+270° endpoint from 360 to 0 for nothing.
      //
      // The MIRRORED pair is rebuilt from the ONE mapped endpoint plus the
      // SOURCE span rather than by mapping both endpoints: the table normalises
      // into `[0, 360)` — deliberately, so a hatch edge's `start == end
      // (mod 360)` full-circle guard keeps firing — so a FULL circle maps both
      // endpoints onto the SAME angle, and `sampleArc` reads an equal pair as a
      // ZERO sweep and draws NOTHING. A reflection preserves the sweep's
      // magnitude and reverses its sense, so the CCW image (a DXF ARC is always
      // drawn CCW start→end — `buildArcSceneGeometry` `ccw: true`, Autodesk DXF
      // reference) runs `map(end) → map(end) + span`.
      const sweepDeg = arcCcwSweepDeg(child.startAngle, child.endAngle);
      const mirroredStart = mirrored
        ? mapUniformArcAngleDeg(child.endAngle, t)
        : 0;
      return {
        ...child,
        ...resolved,
        center: pt(child.center),
        radius: child.radius * radiusScale,
        startAngle: mirrored
          ? mirroredStart
          : child.startAngle + scalarRotationDeg,
        endAngle: mirrored
          ? mirroredStart + sweepDeg
          : child.endAngle + scalarRotationDeg,
      };
    }
    case "Ellipse": {
      // Legacy drops Ellipse children entirely (§3.4#7a). SL-15 round-6 F1 —
      // the transform now carries the FULL 3D geometry (center z, tilted
      // major-axis z, OCS normal) via the one-truth `computeExplodedEllipseGeometry`,
      // so a tilted block-nested ellipse renders in its true plane. `g`
      // overrides center/major/ratio/params/extrusion; a planar child stays
      // byte-identical (major/center z absent, normal back to (0,0,1)).
      const g = transformEllipseGeometry(child, basePoint, t);
      return { ...child, ...resolved, ...g };
    }
    case "Spline": {
      // FIT-POINT mode converts LOCALLY FIRST (@codex #431 round-3): chord-
      // length parametrisation is NOT affine-invariant under non-uniform
      // scale, so interpolating the already-scaled fit points refits a
      // DIFFERENT curve. B-spline control points ARE affine-covariant —
      // convert fit → control in block-local space via the one-truth
      // `fitPointsToControlPoints`, THEN transform the synthesized controls
      // with the insert map (under uniform scale both orders agree — test-
      // proven; only local-first is correct under non-uniform). The clone's
      // tangents are OMITTED: the interpolation consumed them as boundary
      // conditions, so they are baked into the controls (re-emitting them
      // would double-apply downstream — mirrors `splineFromEntity`'s own
      // fit-mode handling for the top-level path).
      if (child.controlPoints.length === 0 && child.fitPoints.length > 0) {
        const derived = fitPointsToControlPoints(
          child.fitPoints,
          child.startTangent,
          child.endTangent,
          // Same clamp `splineFromEntity` applies to the model field.
          Math.max(1, Math.round(child.degree)),
        );
        const {
          startTangent: _omitStart,
          endTangent: _omitEnd,
          ...core
        } = child;
        return {
          ...core,
          ...resolved,
          controlPoints: derived.controlPoints.map((cp) => pt(cp)),
          knots: derived.knots,
          // Transformed for model fidelity; the render shape now rides the
          // synthesized controls (the adapter sees them non-empty → non-fit
          // path, no re-interpolation in scaled space).
          fitPoints: child.fitPoints.map((fp) => pt(fp)),
        };
      }
      // Control-point mode: a NURBS maps through any affine by transforming
      // its control/fit points; knots / weights / degree are affine-
      // invariant. Tangents are directions → linear part only. `weights`
      // ride the `...child` spread unchanged (@codex #431 round-4 —
      // preserved for the render seam; whether they are EVALUATED is the
      // render-wide SceneGeometry limitation, module header "Rational
      // SPLINE weights").
      return {
        ...child,
        ...resolved,
        controlPoints: child.controlPoints.map((cp) => pt(cp)),
        fitPoints: child.fitPoints.map((fp) => pt(fp)),
        ...(child.startTangent
          ? { startTangent: transformBlockDirection(child.startTangent, t) }
          : {}),
        ...(child.endTangent
          ? { endTangent: transformBlockDirection(child.endTangent, t) }
          : {}),
      };
    }
    case "LWPOLYLINE":
      // Legacy dropped bulges + widths in the bag (§3.4#3); typed preserves
      // them: bulge is rotation/uniform-scale invariant and flips sign under
      // mirror; widths follow the |scaleX| magnitude convention under a
      // SIMILARITY and stay AUTHORED under anything else, where the band is
      // built in this frame and mapped instead (see `bandWidthScale`).
      return {
        ...child,
        ...resolved,
        vertices: mapPolylineVertices(child.vertices),
        constantWidth: (child.constantWidth ?? 0) * bandWidthScale,
        elevation: elevationOf(child.elevation),
      };
    case "Text":
      return {
        ...child,
        ...resolved,
        // S10d — carry any per-scale annotative contexts into WORLD space so a
        // block-nested annotative TEXT's active-scale override lands inside the
        // block, not at the drawing origin (round-18 FINDING, textFromEntity:83).
        ...annotativeContextsOverride(
          child,
          basePoint,
          t,
          scalarRotationDeg,
          radiusScale,
        ),
        // Full Vec3 (@codex #431 P2 — Z preserve): the model point is 2D but
        // the typed adapters read an OPTIONAL z (`optionalPointZ`), so the
        // transformed clone carries the block-lifted z like its Line/Circle
        // siblings instead of collapsing text-like children to z=0.
        insertionPoint: pt(child.insertionPoint),
        ...(child.alignmentPoint
          ? { alignmentPoint: pt(child.alignmentPoint) }
          : {}),
        height: child.height * heightScale,
        // Scalar sum — legacy convention, incl. under mirror (see module
        // header "MIRRORED TEXT decision": glyph reflection = S10-XFORM);
        // the round-6 double-negative fold rides `scalarRotationDeg`
        // (S(−s,−s) = R(180°) → the baseline rotates 180° with the block).
        rotation: child.rotation + scalarRotationDeg,
        // @codex #431 P2 — carry the X scale into the glyph metric (module
        // header "TEXT width factor"): exact for axis-aligned text; the
        // rotated-under-non-uniform shear is the S10-XFORM delta.
        widthFactor: (child.widthFactor ?? 1) * widthFactorScale,
      };
    case "MText":
      return {
        ...child,
        ...resolved,
        // S10d — world-space annotative contexts (round-18 FINDING), same as
        // Text above.
        ...annotativeContextsOverride(
          child,
          basePoint,
          t,
          scalarRotationDeg,
          radiusScale,
        ),
        // Full Vec3 — Z preserve, same as Text above (@codex #431 P2).
        insertionPoint: pt(child.insertionPoint),
        nominalHeight: child.nominalHeight * heightScale,
        // Deliberate divergence from legacy: legacy `blockInsert.ts:522`
        // DROPS the child's own rotation (verified bug, plan §4.4 r4). The
        // typed path sums like Text — mode:"reference" for rotated MText —
        // with the round-6 double-negative fold (`scalarRotationDeg`).
        rotation: child.rotation + scalarRotationDeg,
        referenceWidth: (child.referenceWidth ?? 0) * widthScale,
        // @codex #431 P2 — a CUSTOM xAxisDirection (DXF group 11) takes
        // precedence over `rotation` in `effectiveMTextAngleDeg`, so it MUST
        // ride the block linear part (rotation + per-axis scale + mirror) or
        // the child renders at its untransformed direction. The default
        // (1,0,0) stays untouched — the summed `rotation` carries the
        // transform on that path (transforming the default would flip the
        // precedence and change which field drives the render).
        ...(isCustomXAxisDirection(child.xAxisDirection)
          ? { xAxisDirection: transformBlockDirection(child.xAxisDirection, t) }
          : {}),
      };
    case "Hatch":
      // Legacy drops Hatch children (§3.4#7a); union+allow-list arms landed
      // in #249 (S-BLOCKCHILD-HATCH data-loss arm), the render arm rides the
      // typed S6 adapter. Boundary edges transform per edge kind; pattern
      // angle rotates with the INSERT and the pattern scale follows the
      // |scaleX| magnitude convention.
      return {
        ...child,
        ...resolved,
        // S10d — world-space annotative pattern contexts (round-18 FINDING):
        // the hatch `patternAngle`/`patternScale` per-scale override rides the
        // INSERT rotation/scale like the master pattern fields below.
        ...annotativeContextsOverride(
          child,
          basePoint,
          t,
          scalarRotationDeg,
          radiusScale,
        ),
        boundaryPaths: child.boundaryPaths.map((path) => ({
          ...path,
          edges: path.edges.map((edge) =>
            transformHatchEdge(edge, basePoint, t, radiusScale, mirrored),
          ),
        })),
        // @codex #431 P2 + round-3 — the pattern DIRECTION rides the block
        // linear map: mirror reflects it (ANSI31 45° under an X mirror →
        // 135°, exact table) and a NON-uniform scale shears it (45° under
        // scaleX 2 / scaleY 1 → atan2(sin45°, 2·cos45°) ≈ 26.57°, vector
        // branch). Pattern SPACING under non-uniform scale is anisotropic
        // and `patternScale` is a single scalar — documented S10-XFORM
        // residual (module header), the |scaleX| magnitude is the baseline.
        patternAngle: mapDirectionAngleDeg(child.patternAngle ?? 0, t),
        patternScale: (child.patternScale ?? 1) * radiusScale,
        seedPoints: child.seedPoints.map((p) => pt2(p)),
        elevation: elevationOf(child.elevation),
        ...(child.gradient
          ? {
              gradient: {
                ...child.gradient,
                // Gradient rotation is stored in RADIANS (DXF 460) — same
                // direction semantics as patternAngle (@codex #431 P2 +
                // round-3: mirror reflects, non-uniform shears the vector).
                rotation: mapDirectionRad(child.gradient.rotation ?? 0, t),
              },
            }
          : {}),
      };
    case "Revision Cloud": {
      // Stored as an LWPOLYLINE on disk — same vertex/bulge/width treatment;
      // the arc-length scalars follow the |scaleX| magnitude convention.
      // No non-uniform bulge explode here (@codex #431 P2, verified): the
      // typed `revisionCloudFromEntity` renders scallops PROCEDURALLY from
      // the vertex polygon + `approximateArcLength` and never reads vertex
      // bulges, so no bulge-driven circular arc reaches the renderable — the
      // bulge map below is model-fidelity only (mirror sign flip).
      //
      // @codex #431 round-4 — SCALLOP SIDE under a mirror: the cloud builder
      // places scallops on the LEFT of each DIRECTED edge, and vertex-order
      // reversal is its own documented side-control mechanism
      // (`revisionCloudGeometry.ts` — "CCW REVCLOUD reverses the vertex
      // order at construction time so the same routine works for both").
      // A mirror (det < 0) flips traversal handedness, so WITHOUT reversal
      // the scallops land on the geometrically wrong side (inward ↔
      // outward). Reversing the transformed vertex order restores the
      // reflected scallop placement exactly (the sinusoidal bulge is
      // symmetric under traversal reversal — test-locked against a
      // hand-mirrored point set). NB: the top-level MIRROR command has the
      // same-class gap today (`geometryTransforms.ts:1191-1198` mirrors
      // revcloud coords only) — owned by the command path, out of this
      // flatten's scope.
      const cloudVertices = mapPolylineVertices(child.vertices);
      if (mirrored) cloudVertices.reverse();
      return {
        ...child,
        ...resolved,
        vertices: cloudVertices,
        arcChordLength: (child.arcChordLength ?? 0) * radiusScale,
        approximateArcLength: (child.approximateArcLength ?? 0) * radiusScale,
        constantWidth: (child.constantWidth ?? 0) * widthScale,
        elevation: elevationOf(child.elevation),
      };
    }
    case "Polyline": {
      // The legacy heavy POLYLINE — 2D, 3D, polygon mesh and poly-face mesh
      // (@codex PR #785 round 1, F1). All four are point sets plus flags: the
      // vertices ride the affine per {@link mapHeavyPolylineVertex}, the
      // entity-level default widths follow the same |scaleX| magnitude
      // convention the per-vertex ones do, and the 2D PLANE travels on the
      // `elevation` "dummy point" (its X/Y are the file's own, kept verbatim so
      // a non-zero pair still round-trips; only Z is geometric). `flags`,
      // `meshMCount`/`meshNCount`, the surface-type / density fields and the
      // face records are affine-invariant structure and ride the spread.
      //
      // `thickness` — group 39 — is HANDED OVER, not scaled (@codex round 17,
      // FINDING A). Since round 15 `polylineFromEntity` PAINTS the swept
      // surface this kind's group 39 describes, so the stale "no renderer reads
      // it" convention the LWPOLYLINE / Hatch / Revision Cloud arms above still
      // keep for their own group 39 is no longer true here: an unscaled
      // thickness drew a 5-unit sweep inside a uniform ×2 INSERT whose every
      // vertex had already doubled. The image of the sweep is `M·(t·N̂)` — a
      // VECTOR, and under a non-similarity `M` it is not perpendicular to the
      // image PLANE either, so no group-39/group-210 pair on this clone can
      // spell it. `./blockChildThicknessSweep.ts` resolves it once and stamps
      // the world vector `SceneGeometry.polyline.thicknessSweep` already
      // carries; the clone's own scalar is CLEARED against that module's own
      // predicate so the sweep can never be resolved here AND stamped there —
      // the one-truth rule `./blockChildBandFrame.ts` states for the band.
      //
      // @codex round 4, P2 — the child's OCS is consumed BEFORE the INSERT.
      // A 2D child with a non-default group-210 extrusion stores OCS vertices,
      // and the clone used to map those raw numbers through the INSERT while
      // PRESERVING the extrusion, so `polylineFromEntity` performed the lift
      // AFTERWARDS. Lift-then-affine and affine-then-lift do not commute, so
      // the child drew somewhere `wcsBoundsBlock.ts` — which applies
      // `applyOwnOcs` BEFORE the insert chain — did not, and the previous round
      // documented that on `polylineFromEntity.ts` as a residual belonging to
      // this path. `ocsPlanarTransform` is the shape that comment named:
      // ezdxf's `OCSTransform`, lift → affine → re-express in the TRANSFORMED
      // plane's own OCS. It keeps the clone a real 2D polyline, so the bulges
      // survive (a 3D-polyline clone would drop them — group 42 is a
      // 2D-polyline field). The whole INSERT affine is handed over as ONE point
      // map, `pt`, including its z-collapse, which is why the re-expressed
      // plane agrees with the bounds walk exactly. `null` for a default /
      // absent extrusion leaves the byte-identical legacy path below, which the
      // golden-parity harness compares.
      const planar = polylineSubtype(child) === "2d";
      const ocs = planar
        ? ocsPlanarTransform(child.extrusion, child.elevation.z ?? 0, pt)
        : null;
      return {
        ...child,
        ...resolved,
        vertices: child.vertices.map((v) =>
          mapHeavyPolylineVertex(v, planar, ocs),
        ),
        // The sweep travels as a world VECTOR on the renderable (see above);
        // leaving the authored scalar here would make `polylineFromEntity`
        // resolve a SECOND, untransformed one into the geometry and the box.
        ...(blockChildThicknessSweep(child, t) ? { thickness: 0 } : {}),
        ...(child.defaultStartWidth !== undefined
          ? { defaultStartWidth: child.defaultStartWidth * bandWidthScale }
          : {}),
        ...(child.defaultEndWidth !== undefined
          ? { defaultEndWidth: child.defaultEndWidth * bandWidthScale }
          : {}),
        // `extrusion: undefined` CLEARS a group 210 whose image is the WCS
        // default — the plain +Z clone `polylineFromEntity` must NOT re-lift.
        ...(ocs ? { extrusion: ocs.extrusion } : {}),
        elevation: {
          ...child.elevation,
          z: ocs ? ocs.elevation : elevationOf(child.elevation.z),
        },
      };
    }
    case "OLE Frame":
      // Intercepted by `flattenTypedInsert` BEFORE this dispatch and rendered
      // by `./blockChildOleFrame.ts` (@codex PR #785 round 2, F1).
      //
      // An OLE2FRAME stores TWO opposite WCS corners (10/20/30 and 11/21/31)
      // and no rotation — the frame is axis-aligned IN THE FILE by
      // construction (Autodesk DXF reference; ezdxf's `OLE2Frame` implements
      // no `transform()` at all). A CLONE therefore cannot express the image
      // of the rectangle under an oblique INSERT rotation, which is a ROTATED
      // rectangle: this arm used to map all four corners and re-store their
      // axis-aligned BOX, so a 45°-rotated 20x10 frame was DRAWN as the
      // ~21.2 x 21.2 upright box around itself. That box is a legitimate
      // BOUNDS (it never under-reports — the rule `cad-core`'s
      // `blockDefinitionWalk` / `wcsBoundsLeaf.oleFrameLocal` keep for the
      // block-bounds walk, unchanged) but it is not the entity's SHAPE.
      //
      // Inventing a rotation field on the persisted entity to carry it would
      // make the model lie about the file, so the frame is composed at render
      // time instead, exactly like the Dimension / Multileader / Table arms
      // below. `null` here guards a stray direct caller from emitting a frame
      // whose shape was silently squared up.
      return null;
    case "Table":
      // Intercepted by `flattenTypedInsert` before this dispatch and rendered
      // by `./blockChildTable.ts` (module doc above) — an ACAD_TABLE is a block
      // REFERENCE, so the enclosing affine is COMPOSED onto it, not folded into
      // a clone (the entity has no scale field to carry it).
      return null;
    case "Block":
    case "Attdef":
      return null;
    case "Multileader":
      // Intercepted by `flattenTypedInsert` before this dispatch and rendered
      // by `./blockChildMultileader.ts` (module doc above) — the callout is
      // transformed as an ENTITY, not as a leaf clone.
      return null;
    case "Dimension":
      // A block-child DIMENSION is NOT point-transformed + re-derived here.
      // `renderBlockChildDimension` (`blockFromEntity.ts`) generates the
      // dimension in LOCAL space and rigidly transforms each emitted primitive
      // by the block affine — the AutoCAD "*D anonymous-block bake, then
      // transform the INSERT" model. Only that carries the block SCALE into the
      // DIMSTYLE-derived DIMASZ/DIMEXO/DIMEXE/DIMGAP/DIMTXT scalars and the
      // block ROTATION into the horizontal-only linear emitter, and resolves
      // the local `0`-sentinel defPoint / absent-extension-point fallbacks
      // before the affine (@codex #510 ×4). `flattenTypedInsert` intercepts
      // Dimension before this dispatch; null guards a stray direct caller from
      // rendering a wrong point-transformed dimension.
      return null;
    default:
      // Future union growth — mirror the dispatcher's default → null.
      return null;
  }
}
